import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Loader2, Plus, Pencil, Archive, Star, Trash2, FileText, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Research', 'News', 'Announcement', 'Media Resource'];

/* ---------------- Site content (About / Contact / Terms / Privacy) ---------------- */

function AboutEditor({ value, onSave, saving }) {
  const [d, setD] = useState(value);
  useEffect(() => setD(value), [value]);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const setSection = (i, k, v) => setD((p) => {
    const s = [...(p.sections || [])]; s[i] = { ...s[i], [k]: v }; return { ...p, sections: s };
  });
  const setEntry = (si, ei, k, v) => setD((p) => {
    const s = [...(p.sections || [])]; const entries = [...(s[si].entries || [])];
    entries[ei] = { ...entries[ei], [k]: v }; s[si] = { ...s[si], entries }; return { ...p, sections: s };
  });
  const addEntry = (si) => setD((p) => {
    const s = [...(p.sections || [])]; s[si] = { ...s[si], entries: [...(s[si].entries || []), { name: '', title: '', bio: '' }] }; return { ...p, sections: s };
  });
  const removeEntry = (si, ei) => setD((p) => {
    const s = [...(p.sections || [])]; s[si] = { ...s[si], entries: (s[si].entries || []).filter((_, j) => j !== ei) }; return { ...p, sections: s };
  });
  const addTextSection = () => setD((p) => ({
    ...p, sections: [...(p.sections || []), { id: `section-${Date.now()}`, type: 'text', heading: '', body: '', visible: true, order: (p.sections || []).length + 1 }],
  }));
  const removeSection = (i) => setD((p) => ({ ...p, sections: (p.sections || []).filter((_, j) => j !== i) }));

  return (
    <div className="space-y-5" data-testid="about-editor">
      <Field label="Page title"><Input data-testid="about-f-title" value={d.title || ''} onChange={(e) => set('title', e.target.value)} /></Field>
      <Field label="Intro"><Textarea data-testid="about-f-intro" className="min-h-[80px]" value={d.intro || ''} onChange={(e) => set('intro', e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="CTA label"><Input data-testid="about-f-cta-label" value={d.cta_label || ''} onChange={(e) => set('cta_label', e.target.value)} /></Field>
        <Field label="CTA URL"><Input data-testid="about-f-cta-url" value={d.cta_url || ''} onChange={(e) => set('cta_url', e.target.value)} /></Field>
      </div>

      <div className="border-t border-slate-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Sections</h3>
          <Button type="button" variant="outline" size="sm" onClick={addTextSection} data-testid="about-add-section" className="border-slate-300"><Plus className="w-4 h-4 mr-1" /> Add section</Button>
        </div>
        <div className="space-y-4">
          {(d.sections || []).map((sec, i) => (
            <Card key={sec.id || i} className="p-4 border border-slate-200" data-testid={`about-section-edit-${sec.id}`}>
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">{sec.type}</span>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Visible</Label>
                  <Switch checked={sec.visible !== false} onCheckedChange={(v) => setSection(i, 'visible', v)} data-testid={`about-sec-visible-${sec.id}`} />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Order</Label>
                  <Input type="number" className="w-20 h-8" value={sec.order || 0} onChange={(e) => setSection(i, 'order', parseInt(e.target.value || '0', 10))} data-testid={`about-sec-order-${sec.id}`} />
                </div>
                {sec.type === 'text' && (
                  <Button type="button" variant="ghost" size="sm" className="text-red-600 ml-auto" onClick={() => removeSection(i)} data-testid={`about-sec-remove-${sec.id}`}><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
              <Field label="Heading"><Input value={sec.heading || ''} onChange={(e) => setSection(i, 'heading', e.target.value)} data-testid={`about-sec-heading-${sec.id}`} /></Field>
              {sec.type === 'leadership' ? (
                <div className="mt-3 space-y-3">
                  {(sec.entries || []).map((e, ei) => (
                    <div key={ei} className="rounded-lg bg-[#fafafa] border border-slate-100 p-3 space-y-2" data-testid={`about-leader-edit-${ei}`}>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <Input placeholder="Name" value={e.name || ''} onChange={(ev) => setEntry(i, ei, 'name', ev.target.value)} />
                        <Input placeholder="Title" value={e.title || ''} onChange={(ev) => setEntry(i, ei, 'title', ev.target.value)} />
                      </div>
                      <Textarea placeholder="Bio" className="min-h-[70px]" value={e.bio || ''} onChange={(ev) => setEntry(i, ei, 'bio', ev.target.value)} />
                      <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => removeEntry(i, ei)}>Remove person</Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => addEntry(i)} className="border-slate-300"><Plus className="w-4 h-4 mr-1" /> Add person</Button>
                </div>
              ) : (
                <div className="mt-3">
                  <Field label="Body (blank line between paragraphs)"><Textarea className="min-h-[120px] font-mono text-[13px]" value={sec.body || ''} onChange={(e) => setSection(i, 'body', e.target.value)} data-testid={`about-sec-body-${sec.id}`} /></Field>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(d)} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="about-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save About
        </Button>
      </div>
    </div>
  );
}

function ContactEditor({ value, onSave, saving }) {
  const [d, setD] = useState(value);
  useEffect(() => setD(value), [value]);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  return (
    <div className="space-y-5" data-testid="contact-editor">
      <Field label="Page title"><Input data-testid="contact-f-title" value={d.title || ''} onChange={(e) => set('title', e.target.value)} /></Field>
      <Field label="Intro"><Textarea data-testid="contact-f-intro" className="min-h-[70px]" value={d.intro || ''} onChange={(e) => set('intro', e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="General heading"><Input value={d.general_heading || ''} onChange={(e) => set('general_heading', e.target.value)} /></Field>
        <Field label="General contact email"><Input data-testid="contact-f-general-email" value={d.general_email || ''} onChange={(e) => set('general_email', e.target.value)} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Media heading"><Input value={d.media_heading || ''} onChange={(e) => set('media_heading', e.target.value)} /></Field>
        <Field label="Media contact email"><Input data-testid="contact-f-media-email" value={d.media_email || ''} onChange={(e) => set('media_email', e.target.value)} /></Field>
      </div>
      <Field label="Media text"><Textarea className="min-h-[60px]" value={d.media_text || ''} onChange={(e) => set('media_text', e.target.value)} /></Field>
      <Field label="Partnership heading"><Input value={d.partnership_heading || ''} onChange={(e) => set('partnership_heading', e.target.value)} /></Field>
      <Field label="Partnership contact text"><Textarea className="min-h-[80px]" value={d.partnership_text || ''} onChange={(e) => set('partnership_text', e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Partnership CTA label"><Input value={d.partnership_cta_label || ''} onChange={(e) => set('partnership_cta_label', e.target.value)} /></Field>
        <Field label="Partnership CTA URL"><Input value={d.partnership_cta_url || ''} onChange={(e) => set('partnership_cta_url', e.target.value)} /></Field>
      </div>
      <Field label="Support / contact guidance (optional)"><Textarea className="min-h-[60px]" value={d.support_guidance || ''} onChange={(e) => set('support_guidance', e.target.value)} /></Field>
      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(d)} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="contact-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save Contact
        </Button>
      </div>
    </div>
  );
}

function LegalEditor({ value, onSave, saving, keyName }) {
  const [d, setD] = useState(value);
  useEffect(() => setD(value), [value]);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  return (
    <div className="space-y-5" data-testid={`${keyName}-editor`}>
      <Field label="Page title"><Input data-testid={`${keyName}-f-title`} value={d.title || ''} onChange={(e) => set('title', e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Effective date"><Input value={d.effective_date || ''} onChange={(e) => set('effective_date', e.target.value)} /></Field>
        <Field label="Last updated"><Input value={d.last_updated || ''} onChange={(e) => set('last_updated', e.target.value)} /></Field>
      </div>
      <Field label="Body — '## ' heading, '### ' sub-heading, '!! ' legal caps, '- ' list item, blank line between blocks">
        <Textarea data-testid={`${keyName}-f-body`} className="min-h-[420px] font-mono text-[12px] leading-relaxed" value={d.body || ''} onChange={(e) => set('body', e.target.value)} />
      </Field>
      <div className="flex justify-end pt-2">
        <Button onClick={() => onSave(d)} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={`${keyName}-save`}>
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Save
        </Button>
      </div>
    </div>
  );
}

/* ---------------- News & Research ---------------- */

const EMPTY_NEWS = { slug: '', category: 'News', title: '', summary: '', body: '', published_date: '', status: 'draft', featured: false, sort_order: 0, image_url: '', image_alt: '', resource_links: [] };

function NewsEditor({ items, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_NEWS);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setEditing(null); setForm(EMPTY_NEWS); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY_NEWS, ...a }); setOpen(true); };
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setLink = (i, k, v) => setForm((p) => { const l = [...p.resource_links]; l[i] = { ...l[i], [k]: v }; return { ...p, resource_links: l }; });
  const addLink = () => setForm((p) => ({ ...p, resource_links: [...p.resource_links, { label: '', url: '' }] }));
  const removeLink = (i) => setForm((p) => ({ ...p, resource_links: p.resource_links.filter((_, j) => j !== i) }));

  const save = async (status) => {
    setSaving(true);
    const payload = { ...form, status: status || form.status };
    try {
      if (editing) await axios.put(`${API}/api/admin/public-content/news/${editing.slug}`, payload, { withCredentials: true });
      else await axios.post(`${API}/api/admin/public-content/news`, payload, { withCredentials: true });
      toast.success(payload.status === 'published' ? 'Published' : 'Saved');
      setOpen(false); await reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const archive = async (a) => {
    if (!window.confirm(`Archive "${a.title}"? It will be hidden from the public site.`)) return;
    try { await axios.post(`${API}/api/admin/public-content/news/${a.slug}/archive`, {}, { withCredentials: true }); toast.success('Archived'); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Archive failed'); }
  };

  const toggleFeature = async (a) => {
    try { await axios.put(`${API}/api/admin/public-content/news/${a.slug}`, { ...a, featured: !a.featured }, { withCredentials: true }); toast.success(a.featured ? 'Unfeatured' : 'Featured'); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const togglePublish = async (a) => {
    const next = a.status === 'published' ? 'draft' : 'published';
    try { await axios.put(`${API}/api/admin/public-content/news/${a.slug}`, { ...a, status: next }, { withCredentials: true }); toast.success(next === 'published' ? 'Published' : 'Unpublished'); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const statusColor = (s) => s === 'published' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : s === 'archived' ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-amber-50 text-amber-700 border-amber-200';

  return (
    <div data-testid="news-editor">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="bg-brand hover:bg-brand-hover text-white" data-testid="news-new"><Plus className="w-4 h-4 mr-1.5" /> New item</Button>
      </div>
      <div className="space-y-3" data-testid="news-admin-list">
        {items.map((a) => (
          <div key={a.slug} data-testid={`news-row-${a.slug}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-bold uppercase text-brand">{a.category}</span>
                <h3 className="font-semibold text-slate-900 truncate">{a.title}</h3>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusColor(a.status)}`}>{a.status}</span>
                {a.featured && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand/10 text-brand">featured</span>}
              </div>
              <p className="text-xs text-slate-400 mt-0.5 truncate">/{a.slug} {a.published_date ? `· ${a.published_date}` : ''}</p>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              <Button onClick={() => toggleFeature(a)} variant="outline" size="sm" className={`border-slate-200 ${a.featured ? 'text-brand' : ''}`} data-testid={`news-feature-${a.slug}`} title="Feature"><Star className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => togglePublish(a)} variant="outline" size="sm" className="border-slate-200" data-testid={`news-publish-${a.slug}`} title="Publish/Unpublish"><FileText className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => openEdit(a)} variant="outline" size="sm" className="border-slate-200" data-testid={`news-edit-${a.slug}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button onClick={() => archive(a)} variant="outline" size="sm" className="border-slate-200 text-slate-600" data-testid={`news-archive-${a.slug}`} title="Archive"><Archive className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <Card className="p-10 text-center border border-slate-200"><p className="text-slate-500">No items yet.</p></Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="news-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit item' : 'New item'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <Field label="Title"><Input data-testid="news-f-title" value={form.title} onChange={(e) => setF('title', e.target.value)} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Category">
                <select data-testid="news-f-category" value={form.category} onChange={(e) => setF('category', e.target.value)} className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Slug (blank = from title)"><Input data-testid="news-f-slug" value={form.slug} onChange={(e) => setF('slug', e.target.value)} placeholder="auto" /></Field>
            </div>
            <Field label="Short summary"><Textarea data-testid="news-f-summary" className="min-h-[60px]" value={form.summary} onChange={(e) => setF('summary', e.target.value)} /></Field>
            <Field label="Body (blank line between paragraphs)"><Textarea data-testid="news-f-body" className="min-h-[180px] font-mono text-[13px]" value={form.body} onChange={(e) => setF('body', e.target.value)} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Publication date (YYYY-MM-DD)"><Input data-testid="news-f-date" value={form.published_date || ''} onChange={(e) => setF('published_date', e.target.value)} placeholder="2026-10-05" /></Field>
              <Field label="Sort order"><Input type="number" value={form.sort_order} onChange={(e) => setF('sort_order', parseInt(e.target.value || '0', 10))} /></Field>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.featured} onCheckedChange={(v) => setF('featured', v)} data-testid="news-f-featured" />
              <Label className="text-sm">Featured (shown first / on homepage)</Label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Image URL (optional)"><Input data-testid="news-f-image-url" value={form.image_url || ''} onChange={(e) => setF('image_url', e.target.value)} placeholder="https://..." /></Field>
              <Field label="Image alt text (optional)"><Input data-testid="news-f-image-alt" value={form.image_alt || ''} onChange={(e) => setF('image_alt', e.target.value)} placeholder="Describe the image" /></Field>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm">Resource links / buttons</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLink} className="border-slate-300" data-testid="news-add-link"><Plus className="w-4 h-4 mr-1" /> Add link</Button>
              </div>
              <div className="space-y-2">
                {form.resource_links.map((l, i) => (
                  <div key={i} className="flex gap-2" data-testid={`news-link-${i}`}>
                    <Input placeholder="Label" value={l.label} onChange={(e) => setLink(i, 'label', e.target.value)} />
                    <Input placeholder="https://..." value={l.url} onChange={(e) => setLink(i, 'url', e.target.value)} />
                    <Button type="button" variant="ghost" size="sm" className="text-red-600" onClick={() => removeLink(i)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => save('draft')} disabled={saving} className="border-slate-300" data-testid="news-save-draft">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save draft
            </Button>
            <Button onClick={() => save('published')} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid="news-save-publish">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Publish
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-sm text-slate-700">{label}</Label><div className="mt-1">{children}</div></div>;
}

/* ---------------- Page ---------------- */

export default function PublicContent() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('about');
  const [site, setSite] = useState({});
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const loadNews = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/admin/public-content/news`, { withCredentials: true });
    setNews(data.items || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const u = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
        setUser(u.data);
        const keys = ['about', 'contact', 'terms', 'privacy'];
        const results = await Promise.all(keys.map((k) => axios.get(`${API}/api/admin/public-content/site/${k}`, { withCredentials: true }).then((r) => [k, r.data]).catch(() => [k, {}])));
        setSite(Object.fromEntries(results));
        await loadNews();
      } catch (e) { toast.error('Failed to load content'); }
      finally { setLoading(false); }
    })();
  }, [loadNews]);

  const saveSite = async (key, payload) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/public-content/site/${key}`, payload, { withCredentials: true });
      setSite((p) => ({ ...p, [key]: data }));
      toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Layout user={user}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} data-testid="pubc-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Public Content</h1>
          <p className="text-sm text-slate-500">Edit the public About, Contact, News &amp; Research, and legal pages — no code changes needed.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-slate-100 rounded-lg p-1 mb-6 flex-wrap h-auto">
              <TabsTrigger value="about" data-testid="pubc-tab-about" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">About</TabsTrigger>
              <TabsTrigger value="contact" data-testid="pubc-tab-contact" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Contact</TabsTrigger>
              <TabsTrigger value="news" data-testid="pubc-tab-news" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">News &amp; Research</TabsTrigger>
              <TabsTrigger value="terms" data-testid="pubc-tab-terms" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Terms</TabsTrigger>
              <TabsTrigger value="privacy" data-testid="pubc-tab-privacy" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">Privacy</TabsTrigger>
            </TabsList>
            <TabsContent value="about"><AboutEditor value={site.about || {}} onSave={(d) => saveSite('about', d)} saving={saving} /></TabsContent>
            <TabsContent value="contact"><ContactEditor value={site.contact || {}} onSave={(d) => saveSite('contact', d)} saving={saving} /></TabsContent>
            <TabsContent value="news"><NewsEditor items={news} reload={loadNews} /></TabsContent>
            <TabsContent value="terms"><LegalEditor keyName="terms" value={site.terms || {}} onSave={(d) => saveSite('terms', d)} saving={saving} /></TabsContent>
            <TabsContent value="privacy"><LegalEditor keyName="privacy" value={site.privacy || {}} onSave={(d) => saveSite('privacy', d)} saving={saving} /></TabsContent>
          </Tabs>
        )}
      </div>
    </Layout>
  );
}
